import { FaxMediaHooks, FaxMediaQueries } from '@frontend/api-fax-media';
import { getUser } from '@frontend/auth-helpers';
import { EditableText } from '@frontend/editable-text';
import { useTranslation } from '@frontend/i18n';
import { useNavigate } from '@frontend/weave-router';
import { BaseFieldProps, Modal, ModalControlModalProps, useAlert } from '@frontend/design-system';

type Props = {
  fieldProps: BaseFieldProps<string, '', HTMLInputElement>;
  modalProps: ModalControlModalProps;
  locationId: string;
  locationsWithAccess: string[];
  html?: string;
  isCreateTemplate?: boolean;
  onSaveSuccess?: () => void;
};

export const SaveTemplateModal = ({
  fieldProps,
  modalProps,
  locationId,
  html,
  isCreateTemplate,
  locationsWithAccess,
  onSaveSuccess,
}: Props) => {
  const { t } = useTranslation('fax');
  const alert = useAlert();
  const navigate = useNavigate();
  const invalidateFaxTemplateList = FaxMediaHooks.useInvalidateFaxTemplatesList();
  const { mutateAsync: upsertCoversheetTemplate, isLoading: upsertCoversheetTemplateIsLoading } =
    FaxMediaQueries.useUpsertCoversheetTemplate({
      onSuccess: () => {
        invalidateFaxTemplateList();
        alert.success({
          message: t('Template created successfully.'),
        });
      },
      onError: () => {
        alert.error({
          message: t('Error creating template.'),
        });
      },
    });
  const { mutateAsync: renderCoversheet, isLoading: renderCoversheetIsLoading } = FaxMediaQueries.useRenderCoversheet(
    {}
  );
  const user = getUser();

  const currentTime = new Date().toISOString();

  return (
    <Modal {...modalProps} css={{ minWidth: 'min(600px, 95%)' }}>
      <Modal.Header>{t('Name Your New Template')}</Modal.Header>
      <Modal.Body>
        <EditableText fieldProps={fieldProps} helperText={t('Template Title')} />
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Confirm & Save Template'),
          disabled: !fieldProps.value,
          onClick: async () => {
            const renderedCoversheet = await renderCoversheet({
              locationId,
              html,
              populateDynamicFields: false,
            });

            await upsertCoversheetTemplate({
              locationId,
              coversheetTemplate: {
                name: fieldProps.value || '',
                locationIds: locationsWithAccess?.length ? locationsWithAccess : [locationId],
                html,
                createdAt: currentTime,
                updatedAt: currentTime,
                userId: user?.userID || '',
                attributes: renderedCoversheet.attributes || {
                  pages: 1,
                  size: 5,
                },
              },
            });

            modalProps.onClose();
            if (isCreateTemplate) {
              navigate({
                to: '/fax/drafts/new',
                search: {
                  selectedLocationId: locationId,
                },
              });
            }
            onSaveSuccess?.();
          },
          loading: upsertCoversheetTemplateIsLoading || renderCoversheetIsLoading,
        }}
        secondary={{ label: t('Cancel'), onClick: modalProps.onClose }}
      />
    </Modal>
  );
};
